import { Input } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import libphonenumber from 'libphonenumber-js';
import CountryCode from './countryCode.json';

// Liste COMPLÈTE des pays, dans l'ordre du JSON : c'est ELLE qui sert à résoudre
// l'indicatif d'un pays. La liste dédoublonnée ci-dessous ne contient qu'un pays par
// indicatif : tout pays partageant son dial_code avec un autre en disparaît (CA
// derrière US, NO derrière SJ, AU derrière CC, GP, YT…). getPrefixFromCountry
// retombait alors sur son fallback "+33" et validait un numéro canadien contre la
// France → onChange(null) → « Ce champ est obligatoire » insoluble côté visiteur.
const allCountryCodes = [...CountryCode]

// Dédoublonnée par indicatif : UNIQUEMENT pour les options du <select> (un seul
// « +1 » proposé, pas vingt). Ne jamais s'en servir pour résoudre un pays.
const countryCode = CountryCode.sort((a, b) => parseInt(a.dial_code.slice(1)) > parseInt(b.dial_code.slice(1)) ? 1 : -1).filter((v, i, a) => a.findIndex(v2 => (v2.dial_code === v.dial_code)) === i)


const defaultLangToCountry = (_lang) => {
    switch (_lang) {
        case "fr":
            return "FR"
        case "en":
            return "US"
        case "he":
            return "IL"
    }
}


function parseInitialValue(value) {
    if (!value) return { country: null, v: null };
    const pn = libphonenumber(value);
    if (!pn || !pn.isValid()) return { country: null, v: null };
    const countryData = countryCode.find(
        i => i.dial_code === "+" + pn.countryCallingCode
    );
    return {
        country: countryData?.code || null,
        v: pn.nationalNumber || null,
    };
}


async function getCountryByIP() {
    try {
        const cache = sessionStorage.getItem("PHONEINPUT_COUNTRY_IP");
        if (cache) {
            return cache;
        }

        const controller = new AbortController();
        setTimeout(() => controller.abort(), 800);

        const res = await fetch("https://ipapi.co/json/", {
            signal: controller.signal
        });

        if (!res.ok) return null;

        const data = await res.json();

        if (data?.country_code) {
            sessionStorage.setItem("PHONEINPUT_COUNTRY_IP", data.country_code);
            return data.country_code;
        }

        return null;
    } catch (e) {
        return null;
    }
}


export default function PhoneInput({ value, onChange, defaultLang, placeholder, disabled }) {

    const initial = parseInitialValue(value);
    const [country, setCountry] = useState(initial.country);
    const [v, setV] = useState(initial.v);

    const isMountedRef = useRef(false);
    const hasUserTypedRef = useRef(false);
    // Dernière valeur ÉMISE par ce composant (pour distinguer un vidage venu de
    // l'extérieur — copie « Copier depuis… », setFieldValue, reset — de notre
    // propre onChange(null) sur un numéro invalide en cours de saisie).
    const lastEmittedRef = useRef(undefined);

    // INIT COUNTRY
    useEffect(() => {
        if (country) return;

        let cancelled = false;

        (async () => {
            const ipCountry = await getCountryByIP();

            if (cancelled) return;

            if (ipCountry) {
                setCountry(ipCountry);
            } else if (defaultLang) {
                setCountry(defaultLangToCountry(defaultLang));
            } else {
                setCountry("FR");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);



    useEffect(() => {
        if (!value) {
            // Vidé de l'extérieur : effacer l'affichage, sinon l'input montre un
            // numéro que le store ne contient plus (affiché ≠ soumis).
            if (lastEmittedRef.current !== value && v) setV("");
            return;
        }

        const phoneNumber = country ? getPhoneNumber(v, country) : null;

        if (value !== phoneNumber) {
            const _phoneNumber = libphonenumber(value);

            if (_phoneNumber && _phoneNumber.isValid()) {
                const countryData = countryCode.find(
                    i => i.dial_code === "+" + _phoneNumber.countryCallingCode
                );

                if (countryData) {
                    setCountry(countryData.code);
                    setV(_phoneNumber.nationalNumber);
                }
            }
        }
    }, [value]);


    useEffect(() => {
        if (!isMountedRef.current || !country) return;
        if (!hasUserTypedRef.current) return;

        const prefix = getPrefixFromCountry(country);
        const phoneNumber = libphonenumber(prefix + v);

        if (phoneNumber) {
            if (phoneNumber.isValid()) {
                const international = phoneNumber.formatInternational().replaceAll(" ", "");
                lastEmittedRef.current = international;
                onChange(international);
            } else {
                // Numéro invalide pour l'indicatif choisi : ne rien renvoyer de
                // "faux-valide". null = champ vide => le validateur/required du
                // parent affiche un message propre, et les usages sans validateur
                // n'enregistrent aucune valeur cassée. (Avant : NaN, qui se
                // faufilait jusqu'au submit avec un message technique illisible.)
                lastEmittedRef.current = null;
                onChange(null);
            }
        } else if (value) {
            lastEmittedRef.current = null;
            onChange(null);
        }
    }, [v, country]);



    useEffect(() => {
        isMountedRef.current = true;
    }, []);


    const getPrefixFromCountry = (_country = "FR") => {
        // Recherche dans la liste COMPLÈTE : le pays vient de la géoloc IP ou d'un
        // numéro reparsé, il peut donc valoir CA/NO/AU… absents de la liste du select.
        return allCountryCodes.find(i => i.code === _country)?.dial_code || "+33"
    }

    const getPhoneNumber = (_value, _country = "FR") => {
        const prefix = getPrefixFromCountry(_country)

        const phoneNumber = libphonenumber(prefix + _value, _country)

        if (phoneNumber && phoneNumber.isValid()) {
            const international = phoneNumber.formatInternational().replaceAll(" ", "")
            return international
        }

        return null
    }


    const onInputChange = (_value = null) => {
        hasUserTypedRef.current = true;
        let _finalValue = null
        if (_value) {
            _finalValue = _value
        }

        if (_finalValue && _finalValue?.startsWith("0")) {
            _finalValue = _finalValue.slice(1)
        }


        if (_finalValue && _finalValue.startsWith("+")) {
            const phoneNumber = libphonenumber(_finalValue)
            if (phoneNumber && phoneNumber.isValid()) {
                _finalValue = phoneNumber.nationalNumber.replaceAll(" ", "")
                setCountry(phoneNumber.country)
            }
        }

        setV(_finalValue)
    }

    //console.log("COUNTRY",country,getPrefixFromCountry(country))
    //console.log("VALUE",v)

    // Statut de validité du numéro tel que saisi avec l'indicatif courant.
    // L'icône est rendue dans un wrapper TOUJOURS présent (largeur fixe) :
    // Antd ne remonte alors pas l'<input> natif quand l'icône apparaît/change,
    // ce qui évite la perte de focus et la saisie hachée. On ne fait varier que
    // le contenu interne, jamais la présence du suffix.
    const renderStatusIcon = () => {
        let icon = null;
        if (!disabled && v) {
            const phoneNumber = libphonenumber(getPrefixFromCountry(country) + v);
            icon = phoneNumber && phoneNumber.isValid()
                ? <CheckCircleFilled style={{ color: "#52c41a" }} />
                : <CloseCircleFilled style={{ color: "#ff4d4f" }} />;
        }
        return <span style={{ width: 14, display: "inline-flex", justifyContent: "center" }}>{icon}</span>;
    };

    return (
        <div dir="ltr">
        <Input
            disabled={disabled}
            value={v}
            placeholder={placeholder || "6 XX XX XX XX"}
            onChange={(e) => onInputChange(e.target.value)}
            autoComplete="tel"
            suffix={renderStatusIcon()}

            addonBefore={(
                <select
                    disabled={disabled}
                    style={{
                        width: 70,
                        border: 0,
                        outline: 0,
                        background: "transparent",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                        padding: 0,
                        cursor: disabled ? "not-allowed" : "pointer"
                    }}
                    // Le select ne propose qu'une entrée par indicatif : on l'aligne sur
                    // l'entrée canonique du même dial_code (CA → US pour « +1 »). Sinon
                    // aucune option ne correspond, le navigateur affiche la première de
                    // la liste et l'indicatif affiché ment sur l'état réel.
                    value={countryCode.find(i => i.dial_code === getPrefixFromCountry(country))?.code || country}
                    onChange={(e) => {
                        hasUserTypedRef.current = true;
                        setCountry(e.target.value);
                    }}
                >
                    {countryCode.map(i => {
                        return (
                            <option key={i.code} value={i.code}>{i.dial_code}</option>
                        )
                    })}
                </select>
            )}
        />
        </div>
    )
}
